import { useEffect, useLayoutEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';

/** 画面遷移のたびにスクロール位置をどう扱うか */
export type ScrollAction = 'top' | 'restore' | 'keep';

/**
 * 新しい画面を開いたら先頭へ、戻る・進む（とリロード）なら元の位置へ、
 * 同じ画面の URL を置き換えただけ（タブ・絞り込み・ウィザードの手順）なら動かさない。
 */
export const scrollActionFor = (type: NavigationType, samePath: boolean): ScrollAction => {
  if (type === NavigationType.Pop) return 'restore';
  if (type === NavigationType.Replace && samePath) return 'keep';
  return 'top';
};

const STORAGE_KEY = 'scroll-positions';
/** 一覧の読み込みを待つ上限。これを過ぎたら届く範囲まで戻す */
const RESTORE_TIMEOUT_MS = 2000;

const loadPositions = (): Record<string, number> => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, number>) : {};
  } catch {
    return {};
  }
};

/** 保存しておく履歴の項目数。古いものから捨てる */
const MAX_ENTRIES = 100;

const savePositions = (positions: Record<string, number>) => {
  const keys = Object.keys(positions);
  for (const key of keys.slice(0, Math.max(keys.length - MAX_ENTRIES, 0))) delete positions[key];
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
  } catch {
    // プライベートモード等で保存できなくても画面は動かす
  }
};

/**
 * 戻ったときに一覧の位置を復元し、新しい画面は先頭から見せる（docs/08-uiux-design.md 3.3）。
 * ブラウザ任せだと、データ取得前の短い画面に対して復元が走って失敗するため、
 * 画面の高さが足りるまで待ってから戻す。位置は履歴の項目ごとに sessionStorage へ持ち、リロードにも耐える。
 */
export const ScrollRestoration = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const positions = useRef<Record<string, number> | null>(null);
  const currentKey = useRef(location.key);
  const previousPath = useRef(location.pathname);

  if (positions.current === null) positions.current = loadPositions();

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    const record = () => {
      positions.current![currentKey.current] = window.scrollY;
    };
    const persist = () => savePositions(positions.current!);
    window.addEventListener('scroll', record, { passive: true });
    window.addEventListener('pagehide', persist);
    return () => {
      window.removeEventListener('scroll', record);
      window.removeEventListener('pagehide', persist);
    };
  }, []);

  useLayoutEffect(() => {
    const samePath = previousPath.current === location.pathname;
    previousPath.current = location.pathname;
    currentKey.current = location.key;
    savePositions(positions.current!);

    const action = scrollActionFor(navigationType, samePath);
    if (action === 'keep') return;
    if (action === 'top') {
      window.scrollTo(0, 0);
      return;
    }

    const target = positions.current![location.key] ?? 0;
    if (target <= 0) {
      window.scrollTo(0, 0);
      return;
    }

    // 目的の高さまで描画されるのを待つ。利用者が先に動かしたら諦める
    let cancelled = false;
    let frame = 0;
    const startedAt = performance.now();
    const cancel = () => {
      cancelled = true;
    };
    const attempt = () => {
      if (cancelled) return;
      const maxY = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
      if (maxY >= target || performance.now() - startedAt > RESTORE_TIMEOUT_MS) {
        window.scrollTo(0, Math.min(target, maxY));
        return;
      }
      frame = requestAnimationFrame(attempt);
    };
    frame = requestAnimationFrame(attempt);
    window.addEventListener('wheel', cancel, { passive: true, once: true });
    window.addEventListener('touchmove', cancel, { passive: true, once: true });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      window.removeEventListener('wheel', cancel);
      window.removeEventListener('touchmove', cancel);
    };
  }, [location.key, location.pathname, navigationType]);

  return null;
};
